import {useState} from 'react';

import {BrowseCourseProductCard} from '@udemy/browse-course';
import {PAGE_TYPE_SUBS_LANDING_PAGE} from '@udemy/browse-utils';
import {useI18n} from '@udemy/i18n';
import {RecommendationUnit, toBrowseCourse} from '@udemy/react-recommendations';

import {OutlinePill} from './outline-pill.react-component';
import {PlanCatalogPreviewSkeleton} from './plan-catalog-preview-skeleton.react-component';
import styles from './plan-catalog-preview.module.less';
import {useRecommendationsContext} from './use-recommendations-context';

const colors = [
    {
        light: 'var(--color-teal-150)',
        dark: 'var(--color-teal-550)',
    },
    {
        light: 'var(--color-purple-150)',
        dark: 'var(--color-purple-450)',
    },
    {
        light: 'var(--color-gray-150)',
        dark: 'var(--color-gray-450)',
    },
    {
        light: 'var(--color-purple-100)',
        dark: 'var(--color-purple-350)',
    },
    {
        light: 'var(--color-orange-150)',
        dark: 'var(--color-orange-400)',
    },

    {
        light: 'var(--color-teal-100)',
        dark: 'var(--color-teal-550)',
    },
    {
        light: 'var(--color-orange-100)',
        dark: 'var(--color-orange-400)',
    },
    {
        light: 'var(--color-gray-200)',
        dark: 'var(--color-gray-400)',
    },
];

/** Category pill indexes that show the nested "Trending" tag. */
export const DEFAULT_TRENDING_CATEGORY_INDEXES = [0, 1];

interface PlanCatalogPreviewProps {
    /**
     * Category indexes that should render the "Trending" tag.
     * Supports one or many pills, e.g. `[1]` or `[1, 3, 5]`.
     */
    trendingCategoryIndexes?: number[];
}

/**
 * Catalog preview for the Personal Plan landing page.
 * Loads recommendation units for the Personal Plan subscription landing context.
 */
export const PlanCatalogPreview = ({
    trendingCategoryIndexes = DEFAULT_TRENDING_CATEGORY_INDEXES,
}: PlanCatalogPreviewProps) => {
    const {gettext} = useI18n();
    const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<number>(0);
    const trendingIndexSet = new Set(trendingCategoryIndexes);
    const {units, isLoading} = useRecommendationsContext({
        params: {
            contextId: PAGE_TYPE_SUBS_LANDING_PAGE,
        },
    });

    const handleCategoryClick = (index: number) => {
        if (index === selectedCategoryIndex) return;

        setSelectedCategoryIndex(index);
    };

    const color = colors[selectedCategoryIndex];

    if (isLoading) {
        return <PlanCatalogPreviewSkeleton />;
    }

    if (!units?.length) {
        return null;
    }

    const categories: RecommendationUnit[] = units[0]?.units ?? [];
    const selectedCourses =
        categories[selectedCategoryIndex]?.items?.flatMap((item) =>
            item.course ? [toBrowseCourse(item.course)] : [],
        ) ?? [];
    return (
        <div className={styles['plan-catalog-preview']}>
            <div
                className={styles['plan-catalog-preview-bg']}
                style={{backgroundColor: color.light}}
            />
            <div className={styles['plan-catalog-preview-categories']}>
                <div className={styles['plan-catalog-preview-categories-header']}>
                    <h3 className={styles['plan-catalog-preview-categories-title']}>
                        {gettext('Explore what you can learn next')}
                    </h3>
                    <p className={styles['plan-catalog-preview-categories-description']}>
                        {gettext(
                            'From AI and technology to business, leadership, and more, explore expert-led learning for the skills you want to build now—and whatever you want to take on next.',
                        )}
                    </p>
                </div>
                <div className={styles['plan-catalog-preview-categories-list']}>
                    {categories.slice(0, 8).map((category, index) => (
                        <OutlinePill
                            onClick={() => handleCategoryClick(index)}
                            isFilled={selectedCategoryIndex === index}
                            filledBackground={color.dark}
                            backgroundColor={color.light}
                            key={index}
                            title={category.title ?? ''}
                            showTrendingTag={trendingIndexSet.has(index)}
                        />
                    ))}
                </div>
            </div>
            <div className={styles['plan-catalog-preview-courses']}>
                {selectedCourses.slice(0, 4).map((course) => (
                    <div className={styles['plan-catalog-preview-course-card']} key={course.id}>
                        <BrowseCourseProductCard
                            key={course.id}
                            layout="condensed"
                            course={course}
                            showPrice={false}
                            showDescription={false}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};
